// Contratos públicos para que `apps/web` renderice los N bloques de
// la home. La API entrega el label ya proyectado al locale del
// visitante (no LocalizedString) y los eventos hidratados desde el
// Core, listos para pasar a los componentes de Figma sin más
// transformaciones que formateo de fechas/precios.
//
// Los bloques estáticos (artists/cities) se sirven como marcadores
// puros {id, type} — el contenido lo traen sus propios fetchers.
import { z } from 'zod';
import { ImageRefSchema } from './primitives.js';

export const HomeEventPublicSchema = z.object({
  id: z.string().min(1),
  slug: z.string().optional(),
  title: z.string(),
  imageUrl: z.string().optional(),
  startAt: z.string().optional(),
  endAt: z.string().optional(),
  city: z.string().optional(),
  venue: z.string().optional(),
  minPrice: z.number().nonnegative().optional(),
  currency: z.string().optional(),
});
export type HomeEventPublic = z.infer<typeof HomeEventPublicSchema>;

export const HomeCollectionSlotPublicSchema = z.object({
  label: z.string().min(1),
  // Slug de la colección para enlazar a su página. Opcional por
  // compatibilidad con datos antiguos sin slug.
  slug: z.string().optional(),
  // Imagen `thumbnail` (panorámica) de la colección, para el bloque
  // FeaturedCollection de la home.
  image: ImageRefSchema.optional(),
  eventCount: z.number().int().nonnegative(),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
  minPrice: z.number().nonnegative().nullable(),
  currency: z.string().nullable(),
  events: z.array(HomeEventPublicSchema),
});
export type HomeCollectionSlotPublic = z.infer<typeof HomeCollectionSlotPublicSchema>;

/**
 * Bloques públicos de colección. El contenido es idéntico al
 * HomeCollectionSlotPublic anterior, pero envuelto con `id`+`type`.
 */
const FeaturedHeroBlockPublicSchema = HomeCollectionSlotPublicSchema.extend({
  id: z.string().min(1),
  type: z.literal('featuredHero'),
});
const CardsBlockPublicSchema = HomeCollectionSlotPublicSchema.extend({
  id: z.string().min(1),
  type: z.literal('cards'),
});
const FeaturedCollectionBlockPublicSchema = HomeCollectionSlotPublicSchema.extend({
  id: z.string().min(1),
  type: z.literal('featuredCollection'),
});

export const HomeCollectionBlockPublicSchema = z.discriminatedUnion('type', [
  FeaturedHeroBlockPublicSchema,
  CardsBlockPublicSchema,
  FeaturedCollectionBlockPublicSchema,
]);
export type HomeCollectionBlockPublic = z.infer<typeof HomeCollectionBlockPublicSchema>;

/**
 * Bloques estáticos: marcadores puros que la web resuelve con sus
 * propios fetchers (getHomeArtists, getHomeCities).
 */
const ArtistsBlockPublicSchema = z.object({
  id: z.string().min(1),
  type: z.literal('artists'),
});
const CitiesBlockPublicSchema = z.object({
  id: z.string().min(1),
  type: z.literal('cities'),
});

export const HomeStaticBlockPublicSchema = z.discriminatedUnion('type', [
  ArtistsBlockPublicSchema,
  CitiesBlockPublicSchema,
]);
export type HomeStaticBlockPublic = z.infer<typeof HomeStaticBlockPublicSchema>;

// Unión discriminada de las 5 variantes (no `z.union`): así zod valida
// por el discriminante en vez de probar ramas a ciegas, y TypeScript
// estrecha con un `switch (block.type)` sin necesidad de castear en el
// consumidor.
export const HomeBlockPublicSchema = z.discriminatedUnion('type', [
  FeaturedHeroBlockPublicSchema,
  CardsBlockPublicSchema,
  FeaturedCollectionBlockPublicSchema,
  ArtistsBlockPublicSchema,
  CitiesBlockPublicSchema,
]);
export type HomeBlockPublic = z.infer<typeof HomeBlockPublicSchema>;

export const HomeCollectionsPublicResponseSchema = z.object({
  blocks: z.array(HomeBlockPublicSchema),
});
export type HomeCollectionsPublicResponse = z.infer<typeof HomeCollectionsPublicResponseSchema>;

/**
 * Query del endpoint público. `locale` es obligatorio: la API
 * proyecta el label trilingüe a string en ese idioma.
 */
import { LOCALES } from './i18n/locales.js';
export const HomeCollectionsPublicQuerySchema = z.object({
  locale: z.enum(LOCALES),
});
export type HomeCollectionsPublicQuery = z.infer<typeof HomeCollectionsPublicQuerySchema>;
